/**
 * Workshop Log register sections for the Track A net-new registers —
 * Findings (REG-FIND), Opportunities (REG-OPP), Recommendations (REG-REC),
 * and Facilitation Notes (REG-FACNOTES). Each is a self-contained
 * form-plus-list block, built from the same shared controls the original
 * Decisions/Actions/Assumptions sections use (`StatusSelect`,
 * `OwnerCombobox`, `SectionLabel`, `AddButton`, `RemoveButton`,
 * `EmptyHint`, `FIELD_CLASS`) so they read identically. The tabbed
 * `DecisionLogModal` composes them under the "Findings" and "Notes" tabs.
 *
 * Tagging (`<TagInput>`) is wired in here from the start — the registers
 * are tag-native; CC-TAGGING retrofits tags onto the older D/A/A forms and
 * adds the filter rows. Opportunity tags persist `opportunity:<tag>`
 * namespaced (so the Insight Library facets them apart from free-form
 * tags); they are entered/shown bare and namespaced on save.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Lightbulb, NotebookPen, ReceiptText, Sparkles, Telescope } from "lucide-react";
import { cn } from "@/lib/utils";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { StatusSelect, OwnerCombobox } from "./log-controls";
import {
  FIELD_CLASS,
  severityAccent,
  statusAccent,
} from "./log-controls.utils";
import { AddButton, EmptyHint, RemoveButton, SectionLabel } from "./log-rows";
import { TagInput } from "@/components/tags/TagInput";
import { collectTags, resolveTagAccentKey, resolveTagChipStyle } from "@/lib/tags";
import {
  FINDING_DOMAINS,
  FINDING_DOMAIN_DEFAULT,
  FINDING_SEVERITIES,
  FINDING_SEVERITY_DEFAULT,
  FINDING_STATUSES,
  FINDING_STATUS_DEFAULT,
  OPPORTUNITY_STATUSES,
  OPPORTUNITY_TAGS,
  RECOMMENDATION_STATUSES,
  TAG_NS_OPPORTUNITY,
  nsTag,
  stripNs,
} from "@/lib/taxonomy";
import {
  useFindings,
  type FindingDomain,
  type FindingSeverity,
  type FindingStatus,
} from "@/stores/useFindings";
import { useOpportunities, type OpportunityStatus } from "@/stores/useOpportunities";
import {
  useRecommendations,
  type RecommendationStatus,
} from "@/stores/useRecommendations";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";
import { useTranscripts } from "@/stores/useTranscripts";
import {
  buildExtractionPrompt,
  parseCandidateTitles,
  useAiDraft,
  type ExtractionKind,
} from "@/features/xaes-analyst/ai-draft";

/* ── shared row chrome (matches DecisionLogModal) ──────────────────── */

const ROW_CARD: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 8,
  background: "rgba(var(--accent-true-blue-rgb), 0.04)",
  border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
};
const ROW_TITLE: CSSProperties = {
  fontSize: "var(--fs-base)",
  fontWeight: 600,
  color: "var(--text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const ROW_SUB: CSSProperties = {
  fontSize: "var(--fs-label)",
  color: "var(--text-secondary)",
  marginTop: 1,
};
const ROW_META: CSSProperties = {
  fontSize: "var(--fs-label)",
  color: "var(--text-muted)",
  letterSpacing: "0.3px",
  marginTop: 2,
};

/** Read-only tag chips shown on a register row. */
export function RowTags({
  tags,
  format,
  workshopId,
}: {
  tags?: string[];
  format?: (t: string) => string;
  /** Ambient workshop context; literal WS1…WS8 tags use their own accent. */
  workshopId?: string;
}) {
  if (!tags || tags.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
      {tags.map((t) => {
        const accentStyle = resolveTagChipStyle(t, workshopId);
        return (
          <span
            key={t}
            className="tag-chip"
            data-tag-accent={resolveTagAccentKey(t, workshopId)}
            style={{
              fontSize: "var(--fs-micro)",
              color: accentStyle?.color ?? "var(--accent-sky)",
              background:
                accentStyle?.background ?? "rgba(var(--accent-sky-rgb), 0.10)",
              border:
                accentStyle?.border ??
                "1px solid rgba(var(--accent-sky-rgb), 0.22)",
              borderRadius: 999,
              padding: "0 7px",
              lineHeight: 1.6,
            }}
          >
            {format ? format(t) : t}
          </span>
        );
      })}
    </div>
  );
}

type SectionProps = {
  workshopId: string;
  addedBy: string;
  ownerOptions: readonly string[];
};

/* ── Findings (REG-FIND) ───────────────────────────────────────────── */

const EMPTY_ARR: readonly never[] = Object.freeze([]);

export function FindingsSection({ workshopId, addedBy, ownerOptions }: SectionProps) {
  const wsId = workshopId as Workshop["id"];
  const findings = useFindings((s) => s.entriesByWorkshop[wsId] ?? EMPTY_ARR);
  const addFinding = useFindings((s) => s.add);
  const updateFinding = useFindings((s) => s.update);
  const removeFinding = useFindings((s) => s.remove);

  const suggestions = useMemo(() => collectTags(findings), [findings]);

  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [severity, setSeverity] = useState<FindingSeverity>("medium");
  const [domain, setDomain] = useState<FindingDomain>("other");
  const [owner, setOwner] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [spans, setSpans] = useState<string[]>([]);

  function submit(): void {
    const t = title.trim();
    if (!t) return;
    addFinding(wsId, {
      id: crypto.randomUUID(),
      workshopId: wsId,
      title: t,
      detail: detail.trim(),
      severity,
      status: "open",
      domain,
      linkedWorkshopIds: spans,
      owner: owner.trim(),
      createdAt: Date.now(),
      addedBy,
      tags: tags.length ? tags : undefined,
    });
    setTitle("");
    setDetail("");
    setSeverity("medium");
    setDomain("other");
    setOwner("");
    setTags([]);
    setSpans([]);
  }

  return (
    <section>
      <SectionLabel icon={Telescope} text="Findings" />
      <div className="flex flex-col gap-2 pt-2">
        <input
          aria-label="Finding title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Finding — e.g. Approval queue is the main bottleneck"
          className={FIELD_CLASS}
        />
        <input
          aria-label="Finding detail"
          type="text"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder="Detail — what was observed"
          className={FIELD_CLASS}
        />
        <div className="flex flex-wrap gap-2">
          <StatusSelect
            ariaLabel="Finding severity"
            value={severity}
            onValueChange={(v) => setSeverity(v as FindingSeverity)}
            options={FINDING_SEVERITIES}
            accentFor={severityAccent}
            className="w-full sm:w-[120px] sm:flex-none"
          />
          <StatusSelect
            ariaLabel="Finding domain"
            value={domain}
            onValueChange={(v) => setDomain(v as FindingDomain)}
            options={FINDING_DOMAINS}
            accentFor={() => "var(--text-secondary)"}
            className="w-full sm:w-[140px] sm:flex-none"
          />
          <OwnerCombobox
            ariaLabel="Finding owner"
            value={owner}
            onChange={setOwner}
            options={ownerOptions}
            className="min-w-0 w-full flex-1 sm:w-[150px]"
          />
          <AddButton onClick={submit} disabled={!title.trim()} />
        </div>
        <WorkshopSpanToggle current={wsId} value={spans} onChange={setSpans} />
        <TagInput
          value={tags}
          onChange={setTags}
          suggestions={suggestions}
          workshopId={wsId}
          ariaLabel="Finding tags"
        />
      </div>
      <ul className="flex flex-col gap-1.5 pt-3">
        {findings.length === 0 && <EmptyHint text="No findings logged yet." />}
        {findings.map((f) => (
          <li key={f.id} data-record-id={f.id} style={ROW_CARD}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={ROW_TITLE}>{f.title}</div>
              {f.detail && <div style={ROW_SUB}>{f.detail}</div>}
              <div style={ROW_META}>
                <span style={{ color: severityAccent(f.severity), fontWeight: 600 }}>
                  {f.severity}
                </span>
                {` · ${f.domain}`}
                {f.owner ? ` · ${f.owner}` : ""}
                {f.linkedWorkshopIds.length > 0
                  ? ` · spans ${f.linkedWorkshopIds.join(", ")}`
                  : ""}
              </div>
              <RowTags tags={f.tags} workshopId={wsId} />
            </div>
            <StatusSelect
              ariaLabel={`Status of finding: ${f.title}`}
              value={f.status}
              onValueChange={(v) => updateFinding(wsId, f.id, { status: v as FindingStatus })}
              options={FINDING_STATUSES}
              accentFor={statusAccent}
              className="w-[136px] flex-none"
            />
            <RemoveButton label={`Remove finding: ${f.title}`} onClick={() => removeFinding(wsId, f.id)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Compact toggle row for "this finding also spans …" — the other
 *  workshops a cross-workshop theme touches. */
function WorkshopSpanToggle({
  current,
  value,
  onChange,
}: {
  current: string;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const others = WORKSHOPS.filter((w) => w.id !== current);
  const set = new Set(value);
  return (
    <div
      role="group"
      aria-label="Other workshops this finding spans"
      style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}
    >
      <span style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)", letterSpacing: 1 }}>
        ALSO SPANS
      </span>
      {others.map((w) => {
        const on = set.has(w.id);
        return (
          <button
            key={w.id}
            type="button"
            aria-pressed={on}
            title={w.longTitle}
            onClick={() =>
              onChange(on ? value.filter((id) => id !== w.id) : [...value, w.id])
            }
            style={{
              fontSize: "var(--fs-micro)",
              padding: "1px 8px",
              borderRadius: 999,
              cursor: "pointer",
              border: on
                ? "1px solid var(--accent-true-blue)"
                : "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
              background: on ? "rgba(var(--accent-true-blue-rgb), 0.16)" : "transparent",
              color: on ? "var(--text-primary)" : "var(--text-muted)",
            }}
          >
            {w.id}
          </button>
        );
      })}
    </div>
  );
}

/* ── Opportunities (REG-OPP) ───────────────────────────────────────── */

export function OpportunitiesSection({ workshopId, addedBy, ownerOptions }: SectionProps) {
  const wsId = workshopId as Workshop["id"];
  const opportunities = useOpportunities((s) => s.entriesByWorkshop[wsId] ?? EMPTY_ARR);
  const addOpportunity = useOpportunities((s) => s.add);
  const updateOpportunity = useOpportunities((s) => s.update);
  const removeOpportunity = useOpportunities((s) => s.remove);

  // Suggestions are shown bare even though storage is namespaced: the
  // curated OPPORTUNITY_TAGS plus any already in use across this workshop.
  const suggestions = useMemo(() => {
    const used = collectTags(opportunities).map((t) => stripNs(TAG_NS_OPPORTUNITY, t));
    return Array.from(new Set([...OPPORTUNITY_TAGS, ...used]));
  }, [opportunities]);

  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [owner, setOwner] = useState("");
  const [tags, setTags] = useState<string[]>([]);

  function submit(): void {
    const t = title.trim();
    if (!t) return;
    addOpportunity(wsId, {
      id: crypto.randomUUID(),
      workshopId: wsId,
      title: t,
      detail: detail.trim(),
      status: "identified",
      owner: owner.trim(),
      createdAt: Date.now(),
      addedBy,
      // Persist opportunity tags namespaced so the Insight Library facets
      // them distinctly from free-form tags.
      tags: tags.length ? tags.map((tag) => nsTag(TAG_NS_OPPORTUNITY, tag)) : undefined,
    });
    setTitle("");
    setDetail("");
    setOwner("");
    setTags([]);
  }

  return (
    <section>
      <SectionLabel icon={Lightbulb} text="Opportunities" />
      <div className="flex flex-col gap-2 pt-2">
        <input
          aria-label="Opportunity title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Opportunity — e.g. Automate low-risk intake triage"
          className={FIELD_CLASS}
        />
        <div className="flex flex-wrap gap-2">
          <input
            aria-label="Opportunity detail"
            type="text"
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Detail — the opportunity"
            className={cn(FIELD_CLASS, "min-w-0 flex-1")}
          />
          <OwnerCombobox
            ariaLabel="Opportunity owner"
            value={owner}
            onChange={setOwner}
            options={ownerOptions}
            className="w-full sm:w-[150px] sm:flex-none"
          />
          <AddButton onClick={submit} disabled={!title.trim()} />
        </div>
        <TagInput
          value={tags}
          onChange={setTags}
          suggestions={suggestions}
          workshopId={wsId}
          ariaLabel="Opportunity tags"
          placeholder="Add opportunity tag…"
        />
      </div>
      <ul className="flex flex-col gap-1.5 pt-3">
        {opportunities.length === 0 && <EmptyHint text="No opportunities identified yet." />}
        {opportunities.map((o) => (
          <li key={o.id} data-record-id={o.id} style={ROW_CARD}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={ROW_TITLE}>{o.title}</div>
              {o.detail && <div style={ROW_SUB}>{o.detail}</div>}
              <div style={ROW_META}>
                {o.owner ? `${o.owner} · ` : ""}
                {o.addedBy}
              </div>
              <RowTags
                tags={o.tags}
                workshopId={wsId}
                format={(t) => stripNs(TAG_NS_OPPORTUNITY, t)}
              />
            </div>
            <StatusSelect
              ariaLabel={`Status of opportunity: ${o.title}`}
              value={o.status}
              onValueChange={(v) => updateOpportunity(wsId, o.id, { status: v as OpportunityStatus })}
              options={OPPORTUNITY_STATUSES}
              accentFor={statusAccent}
              className="w-[128px] flex-none"
            />
            <RemoveButton label={`Remove opportunity: ${o.title}`} onClick={() => removeOpportunity(wsId, o.id)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Recommendations (REG-REC) ─────────────────────────────────────── */

export function RecommendationsSection({ workshopId, addedBy, ownerOptions }: SectionProps) {
  const wsId = workshopId as Workshop["id"];
  const recommendations = useRecommendations((s) => s.entriesByWorkshop[wsId] ?? EMPTY_ARR);
  const addRecommendation = useRecommendations((s) => s.add);
  const updateRecommendation = useRecommendations((s) => s.update);
  const removeRecommendation = useRecommendations((s) => s.remove);

  const suggestions = useMemo(() => collectTags(recommendations), [recommendations]);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [owner, setOwner] = useState("");
  const [tags, setTags] = useState<string[]>([]);

  function submit(): void {
    const t = title.trim();
    if (!t) return;
    addRecommendation(wsId, {
      id: crypto.randomUUID(),
      workshopId: wsId,
      title: t,
      body: body.trim(),
      status: "draft",
      owner: owner.trim(),
      createdAt: Date.now(),
      addedBy,
      tags: tags.length ? tags : undefined,
    });
    setTitle("");
    setBody("");
    setOwner("");
    setTags([]);
  }

  return (
    <section>
      <SectionLabel icon={ReceiptText} text="Recommendations" />
      <div className="flex flex-col gap-2 pt-2">
        <input
          aria-label="Recommendation title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Recommendation — e.g. Adopt a staged rollout"
          className={FIELD_CLASS}
        />
        <textarea
          aria-label="Recommendation body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Body — the rationale and proposed course (AI can draft this later)"
          rows={2}
          className={cn(FIELD_CLASS, "resize-y")}
        />
        <div className="flex flex-wrap gap-2">
          <OwnerCombobox
            ariaLabel="Recommendation owner"
            value={owner}
            onChange={setOwner}
            options={ownerOptions}
            className="w-full sm:w-[150px] sm:flex-none"
          />
          <AddButton onClick={submit} disabled={!title.trim()} />
        </div>
        <TagInput
          value={tags}
          onChange={setTags}
          suggestions={suggestions}
          workshopId={wsId}
          ariaLabel="Recommendation tags"
        />
      </div>
      <ul className="flex flex-col gap-1.5 pt-3">
        {recommendations.length === 0 && <EmptyHint text="No recommendations drafted yet." />}
        {recommendations.map((r) => (
          <li key={r.id} data-record-id={r.id} style={ROW_CARD}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={ROW_TITLE}>{r.title}</div>
              {r.body && <div style={ROW_SUB}>{r.body}</div>}
              <div style={ROW_META}>
                {r.owner ? `${r.owner} · ` : ""}
                {r.addedBy}
              </div>
              <RowTags tags={r.tags} workshopId={wsId} />
            </div>
            <StatusSelect
              ariaLabel={`Status of recommendation: ${r.title}`}
              value={r.status}
              onValueChange={(v) => updateRecommendation(wsId, r.id, { status: v as RecommendationStatus })}
              options={RECOMMENDATION_STATUSES}
              accentFor={statusAccent}
              className="w-[128px] flex-none"
            />
            <RemoveButton label={`Remove recommendation: ${r.title}`} onClick={() => removeRecommendation(wsId, r.id)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Facilitation notes (REG-FACNOTES) ─────────────────────────────── */

export function NotesSection({ workshopId, addedBy }: SectionProps) {
  const wsId = workshopId as Workshop["id"];
  const notes = useFacilitationNotes((s) => s.entriesByWorkshop[wsId] ?? EMPTY_ARR);
  const addNote = useFacilitationNotes((s) => s.add);
  const removeNote = useFacilitationNotes((s) => s.remove);

  const suggestions = useMemo(() => collectTags(notes), [notes]);

  const [body, setBody] = useState("");
  const [tags, setTags] = useState<string[]>([]);

  function submit(): void {
    const b = body.trim();
    if (!b) return;
    addNote(wsId, {
      id: crypto.randomUUID(),
      workshopId: wsId,
      body: b,
      createdAt: Date.now(),
      addedBy,
      tags: tags.length ? tags : undefined,
    });
    setBody("");
    setTags([]);
  }

  return (
    <section>
      <SectionLabel icon={NotebookPen} text="Facilitation notes" />
      <div className="flex flex-col gap-2 pt-2">
        <textarea
          aria-label="Facilitation note"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Note — e.g. Room pushed back on the budget framing; revisit next session"
          rows={3}
          className={cn(FIELD_CLASS, "resize-y")}
        />
        <div className="flex flex-wrap items-center gap-2">
          <TagInput
            value={tags}
            onChange={setTags}
            suggestions={suggestions}
            workshopId={wsId}
            ariaLabel="Note tags"
            className="flex-1"
          />
          <AddButton onClick={submit} disabled={!body.trim()} label="ADD NOTE" />
        </div>
      </div>
      <ul className="flex flex-col gap-1.5 pt-3">
        {notes.length === 0 && <EmptyHint text="No facilitation notes yet." />}
        {notes.map((n) => (
          <li key={n.id} data-record-id={n.id} style={{ ...ROW_CARD, alignItems: "flex-start" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ ...ROW_SUB, color: "var(--text-primary)", whiteSpace: "pre-wrap" }}>
                {n.body}
              </div>
              <div style={ROW_META}>{n.addedBy}</div>
              <RowTags tags={n.tags} workshopId={wsId} />
            </div>
            <RemoveButton
              label="Remove note"
              onClick={() => removeNote(wsId, n.id)}
              style={{ alignSelf: "flex-start" }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── AI insight extraction (AI-EXTRACTION) ─────────────────────────── */

/**
 * Opt-in AI extraction: pick a captured transcript and a target register
 * (findings or opportunities), stream candidate titles from it, then **Add**
 * the ones worth keeping. Human-in-the-loop — nothing is written until a
 * per-row Add (each title editable first), and keyless it streams the
 * explicit mock banner. Candidates are tagged `ai-extracted` so they're
 * auditable (namespaced `opportunity:ai-extracted` on the opportunity path so
 * the Insight Library facets them with the other opportunity tags).
 */
export function AiExtractionSection({ workshopId, addedBy }: SectionProps) {
  const wsId = workshopId as Workshop["id"];
  const transcripts = useTranscripts((s) => s.entriesByWorkshop[wsId] ?? EMPTY_ARR);
  const addFinding = useFindings((s) => s.add);
  const addOpportunity = useOpportunities((s) => s.add);
  const ai = useAiDraft();

  const [transcriptId, setTranscriptId] = useState("");
  const [kind, setKind] = useState<ExtractionKind>("finding");
  // Candidates carry a stable id so removing one mid-list keeps React keys +
  // per-row input state aligned (no index-as-key reconciliation bug).
  const [candidates, setCandidates] = useState<Array<{ id: string; title: string }>>([]);

  useEffect(() => {
    void useTranscripts.getState().hydrate();
  }, []);

  const selected = transcripts.find((t) => t.id === transcriptId);
  const streaming = ai.status === "streaming";
  const noun = kind === "opportunity" ? "opportunity" : "finding";

  async function extract(): Promise<void> {
    if (!selected) return;
    setCandidates([]);
    const { system, prompt } = buildExtractionPrompt(wsId, selected.body, kind);
    const draft = await ai.run(system, prompt);
    setCandidates(
      parseCandidateTitles(draft).map((title) => ({ id: crypto.randomUUID(), title })),
    );
  }

  function addCandidate(id: string): void {
    const title = candidates.find((c) => c.id === id)?.title.trim();
    if (!title) return;
    if (kind === "opportunity") {
      addOpportunity(wsId, {
        id: crypto.randomUUID(),
        workshopId: wsId,
        title,
        detail: "",
        status: "identified",
        owner: "",
        createdAt: Date.now(),
        addedBy,
        tags: [nsTag(TAG_NS_OPPORTUNITY, "ai-extracted")],
      });
    } else {
      addFinding(wsId, {
        id: crypto.randomUUID(),
        workshopId: wsId,
        title,
        detail: "",
        severity: FINDING_SEVERITY_DEFAULT,
        status: FINDING_STATUS_DEFAULT,
        domain: FINDING_DOMAIN_DEFAULT,
        linkedWorkshopIds: [],
        owner: "",
        createdAt: Date.now(),
        addedBy,
        tags: ["ai-extracted"],
      });
    }
    setCandidates((prev) => prev.filter((c) => c.id !== id));
  }

  return (
    <section>
      <SectionLabel icon={Sparkles} text="AI insight extraction" />
      <div className="flex flex-col gap-2 pt-2">
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Transcript to extract from"
            value={transcriptId}
            onChange={(e) => setTranscriptId(e.target.value)}
            disabled={transcripts.length === 0}
            className={cn(FIELD_CLASS, "min-w-0 flex-1")}
            style={transcripts.length === 0 ? { opacity: 0.5 } : undefined}
          >
            <option value="">Choose a transcript…</option>
            {transcripts.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
          <select
            aria-label="Extract as"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as ExtractionKind);
              setCandidates([]);
            }}
            className={cn(FIELD_CLASS, "w-full sm:w-[150px] sm:flex-none")}
          >
            <option value="finding">Findings</option>
            <option value="opportunity">Opportunities</option>
          </select>
          <AddButton
            onClick={() => void extract()}
            disabled={!selected || streaming}
            label={streaming ? "EXTRACTING…" : "EXTRACT"}
          />
        </div>
        {transcripts.length === 0 && (
          <EmptyHint text="Capture a transcript first (in the Repository) to extract insights." />
        )}
        {!ai.configured && (
          <span style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)" }}>
            Runs in mock mode — set VITE_ANTHROPIC_API_KEY for real extraction.
          </span>
        )}
        {ai.error && (
          <p style={{ fontSize: "var(--fs-micro)", color: "var(--accent-red)" }}>{ai.error}</p>
        )}
      </div>
      {candidates.length > 0 && (
        <ul className="flex flex-col gap-1.5 pt-3">
          {candidates.map((c, i) => (
            <li key={c.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                aria-label={`Candidate ${noun} ${i + 1}`}
                value={c.title}
                onChange={(e) =>
                  setCandidates((prev) =>
                    prev.map((x) => (x.id === c.id ? { ...x, title: e.target.value } : x)),
                  )
                }
                className={cn(FIELD_CLASS, "flex-1")}
              />
              <AddButton onClick={() => addCandidate(c.id)} disabled={!c.title.trim()} label="ADD" />
              <RemoveButton
                label={`Dismiss candidate: ${c.title}`}
                onClick={() => setCandidates((prev) => prev.filter((x) => x.id !== c.id))}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Lazy-hydrate every Track A register this Log surfaces. Idempotent. */
export function useHydrateRegisters(open: boolean): void {
  useEffect(() => {
    if (!open) return;
    void useFindings.getState().hydrate();
    void useOpportunities.getState().hydrate();
    void useRecommendations.getState().hydrate();
    void useFacilitationNotes.getState().hydrate();
  }, [open]);
}
